import { onError } from '@apollo/client/link/error';
import { captureException, withScope } from '@sentry/browser';
import axios from 'axios';
import { nhost } from 'Providers/nhost/NhostProvider.tsx';
import { useToast } from 'utils/hooks/useToast';

const addAxiosInterceptors = () => {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      withScope((scope) => {
        scope.setContext('axiosError', {
          response: error.response,
          request: error.request,
          config: error.config,
        });
        captureException(error);
      });
      return Promise.reject(error);
    }
  );
};

const ignoreErrorCodes = ['invalid-jwt'];
const allowListErrorCodes = ['validation-failed']; // Hasura allowlist error code

const addGraphlQLErrorHandling = () => {
  const toast = useToast();

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach((graphQLError) => {
        const { message, extensions } = graphQLError;

        withScope((scope) => {
          scope.setContext('graphQLError', {
            message,
            locations: graphQLError.locations,
            path: graphQLError.path,
            extensions,
          });
          scope.setFingerprint([operation.operationName]);

          if (extensions?.code && ignoreErrorCodes.includes(extensions.code as string)) {
            //Session expired - try to refresh token silently
            try {
              nhost.refreshSession(10);
            } catch (e) {
              toast({
                text: 'Your session has expired. Please log in again.',
                variant: 'danger',
              });
              nhost.clearSession();
            }
          } else if (extensions?.code && allowListErrorCodes.includes(extensions.code as string)) {
            // Sometimes theres a mismatch between frontend and backend allowlist so the user has to refresh.
            // This code detects if this error occured and then refreshes automatically for the user. Not the best UX but better than nothing.
            // the allowListReloaded check will stop any infinte reloading loops
            if (!sessionStorage.getItem('allowlistReloaded')) {
              sessionStorage.setItem('allowlistReloaded', 'true');

              toast({
                text: 'App updated. Refreshing...',
              });

              setTimeout(() => {
                window.location.reload();
              }, 1500);
            }
          } else {
            captureException(new Error(`GraphQL Error: ${operation.operationName}`), {
              fingerprint: [operation.operationName],
              extra: { graphQLError },
            });
          }
        });
      });
    }

    if (networkError) {
      toast({
        text: 'Could not connect to server. Please try again later.',
        variant: 'danger',
      });
    }
  });

  return { apolloErrorLink: errorLink };
};

export const useInitErrorHandling = () => {
  addAxiosInterceptors();
  const { apolloErrorLink } = addGraphlQLErrorHandling();
  return { apolloErrorLink };
};
